import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GraduationCap, Eye, EyeOff, ArrowLeft, Lock, Mail, Globe2, Award, BookOpen } from 'lucide-react';
import { useAuth } from '../App';
import { PublicFooter } from '../components/public/PublicLayout';

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(formData.email, formData.password);
    if (result?.success) {
      const role = result.user?.user_type;
      // Students go back to the page they were on (e.g. a /courses search)
      // or land on the public homepage — NOT the admin dashboard.
      const params = new URLSearchParams(location.search);
      const nextQ = params.get('next');
      const returnTo = nextQ || location.state?.from?.pathname || location.state?.returnTo;
      if (role === 'student') {
        const target = returnTo && !returnTo.startsWith('/dashboard') ? returnTo : '/';
        navigate(target, { replace: true });
      } else if (role === 'agent') {
        navigate('/agent');
      } else {
        navigate('/dashboard');
      }
    } else if (result?.error) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-160px)]">
        {/* Left: branded gradient panel with mission + stats */}
        <aside className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A2A4F] via-[#243a6a] to-[#1A2A4F]" />
          {/* Orange glow accent */}
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#F5A623]/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-[#F5A623]/15 blur-3xl pointer-events-none" />

          <div className="relative">
            <Link to="/" className="inline-flex items-center gap-2.5" data-testid="login-back-home-brand">
              <div className="w-11 h-11 rounded-xl bg-[#F5A623] flex items-center justify-center shadow-lg shadow-[#F5A623]/30">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-lg leading-tight">EduBridge Network</p>
                <p className="text-xs opacity-70">Education Admissions CRM</p>
              </div>
            </Link>
          </div>

          <div className="relative">
            <p className="text-[#F5A623] font-semibold tracking-widest text-xs uppercase">Welcome back</p>
            <h2 className="mt-3 text-4xl xl:text-5xl font-bold leading-tight">
              Your study-abroad<br />journey continues here.
            </h2>
            <p className="mt-5 text-sm xl:text-base opacity-80 max-w-md leading-relaxed">
              Sign in to track applications, message your counsellor, upload documents,
              and stay one step ahead of every deadline.
            </p>
          </div>

          <div className="relative grid grid-cols-3 gap-4">
            <StatPill icon={<BookOpen className="w-4 h-4" />} value="500+" label="Programs" />
            <StatPill icon={<Globe2 className="w-4 h-4" />} value="10+" label="Countries" />
            <StatPill icon={<Award className="w-4 h-4" />} value="60+" label="Universities" />
          </div>
        </aside>

        {/* Right: the form */}
        <section className="flex items-center justify-center px-4 sm:px-10 py-12 bg-gradient-to-br from-[#F5A623]/5 to-white">
          <div className="w-full max-w-md">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2A4F] hover:text-[#F5A623] mb-8" data-testid="login-back-home">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>

            {/* Mobile-only logo */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-[#F5A623] flex items-center justify-center shadow-lg">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-lg leading-tight text-[#1A2A4F]">EduBridge Network</p>
                <p className="text-xs text-gray-500">Education Admissions CRM</p>
              </div>
            </div>

            <h1 className="text-3xl font-bold text-[#1A2A4F]">Sign in to your account</h1>
            <p className="mt-2 text-sm text-gray-500">Welcome back — let's get you to your dashboard.</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
              <div>
                <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">Email address</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-3 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none transition"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-[#1A2A4F]">Password</label>
                  <Link to="/forgot-password" className="text-xs font-semibold text-[#F5A623] hover:underline" data-testid="login-forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password"
                    className="w-full border border-gray-200 rounded-lg pl-10 pr-12 py-3 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none transition"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1A2A4F]"
                    data-testid="login-pw-toggle"
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#F5A623] text-white font-bold py-3 rounded-lg hover:brightness-95 disabled:opacity-60 transition flex items-center justify-center gap-2 shadow-lg shadow-[#F5A623]/30"
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="mt-6 text-sm text-center text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-bold text-[#F5A623] hover:underline" data-testid="login-signup-link">
                Create one free
              </Link>
            </p>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
};

const StatPill = ({ icon, value, label }) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3.5 border border-white/15">
    <div className="text-[#F5A623]">{icon}</div>
    <p className="mt-2 text-xl font-bold">{value}</p>
    <p className="text-xs opacity-70">{label}</p>
  </div>
);

export default LoginPage;
